import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import ActionButton from '../components/ui/ActionButton';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/ar-invoices`;
const PAGE_SIZE = 50;

interface Obligation { id: number; po_number: number; description: string; obligation_name?: string; obligation_type: string; standalone_selling_price: number; allocated_price: number; amount_recognized: number; amount_deferred?: number | null; recognition_method: string; fulfillment_trigger?: string | null; fulfilled_at?: string | null; fulfilled_by?: number | null; status: string; }
interface Contract { id: number; contract_number: string; customer_id: string; customer_name: string; total_value: number; currency: string; status: string; start_date: string; end_date: string; asc606_step1_met: boolean; obligations: Obligation[]; }
interface ScheduleLine { id: number; period: string; scheduled_amount: number; recognized_amount: number; status: string; }
interface Schedule { obligation_id: number; description: string; recognition_method: string; allocated_price: number; total_scheduled: number; total_recognized: number; total_remaining: number; pct_recognized: number; lines: ScheduleLine[]; }
interface WaterfallPeriod { period: string; scheduled: number; recognized: number; deferred_balance: number; projected_deferred?: number; actual_deferred?: number; status: string; }
interface WaterfallOb { obligation_id: number; description: string; allocated_price: number; amount_recognized: number; amount_remaining: number; status: string; periods: WaterfallPeriod[]; }
interface Waterfall {
  contract_id: number;
  contract_number: string;
  total_value: number;
  total_recognized: number;
  total_deferred: number;          // legacy field (total_value − recognized)
  pct_recognized: number;
  obligations: WaterfallOb[];
  // Phase 3c-ext: MSA-aware fields
  is_msa?: boolean;
  source_contract_subtype?: string | null;
  committed_amount?: number;       // MSA ceiling (same as total_value for non-MSA)
  allocated_to_pobs?: number;      // drawn amount (sum of POB allocated_price)
  true_deferred?: number;          // allocated_to_pobs − recognized (real ASC 606 deferred)
  unutilized_ceiling?: number;     // committed − allocated (MSA only, 0 for non-MSA)
}

const thStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', padding: '10px 14px', color: 'var(--text-hint)', letterSpacing: '0.04em', borderBottom: '1px solid var(--border-subtle)' };
const tdStyle: React.CSSProperties = { fontSize: 13, padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' };

const REV_API = `${API_V1}/revenue`;

interface JELineRow {
  line_number: number;
  account_code: string;
  description: string;
  debit_amount: number;
  credit_amount: number;
  currency: string;
}
interface JERow {
  id: number;
  je_number: string;
  entity_code: string;
  posting_date: string;
  description: string;
  je_type: string;
  source_type: string;
  source_reference: string;
  category: 'BILLING' | 'RECOGNITION';
  currency: string;
  posted_by: string;
  je_status: string;
  total_amount: number;
  lines: JELineRow[];
}
interface JEList {
  contract_id: number;
  contract_number: string;
  count: number;
  journal_entries: JERow[];
}

export default function RevenueRecognition() {
  const { entity } = useGlobalEntity();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [waterfall, setWaterfall] = useState<Waterfall | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  // Phase 2f Confirm Milestone state
  const [fulfillTarget, setFulfillTarget] = useState<{ ob: Obligation; contract: Contract } | null>(null);
  const [fulfillDate, setFulfillDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [fulfillNotes, setFulfillNotes] = useState('');
  const [fulfilling, setFulfilling] = useState(false);
  // Phase 2f JE audit view state
  const [jeList, setJeList] = useState<JEList | null>(null);
  const [expandedJe, setExpandedJe] = useState<number | null>(null);
  // Phase 3c-ext UX: collapse contract POB tables by default. Click a row to expand.
  const [expandedContractId, setExpandedContractId] = useState<number | null>(null);

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(withEntityParam(`${API}/contracts?limit=${PAGE_SIZE}`, entity));
      if (res.ok) { const d = await res.json(); setContracts(d.items || []); setTotal(d.total || 0); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async (obligationId: number) => {
    try {
      const res = await authFetch(`${API}/schedules/${obligationId}/generate`, { method: 'POST' });
      if (res.ok) { showToast('Schedule generated', true); viewSchedule(obligationId); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Failed', false); }
  };

  const viewSchedule = async (obligationId: number) => {
    try {
      const res = await authFetch(`${API}/schedules/${obligationId}`);
      if (res.ok) { setSelectedSchedule(await res.json()); }
      else { showToast('No schedule found — generate first', false); }
    } catch { showToast('Failed to load schedule', false); }
  };

  const handleRecognize = async (obligationId: number, period: string) => {
    try {
      const res = await authFetch(`${API}/schedules/${obligationId}/recognize`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ period }) });
      if (res.ok) { showToast(`Recognized ${period}`, true); viewSchedule(obligationId); }
      else {
        // Phase 10c (2026-05-25): surface HTTP status + backend detail
        // so silent failures (500 with no body, JE-post errors) stop
        // collapsing to a generic "Failed" toast. Matches the
        // user-facing error-translation pattern.
        let detail = '';
        try {
          const e = await res.json();
          detail = e.detail || e.message || JSON.stringify(e);
        } catch {
          detail = await res.text().catch(() => res.statusText);
        }
        showToast(`Recognize failed (${res.status}): ${detail}`, false);
      }
    } catch (e: any) {
      showToast(`Recognize failed: ${e?.message || 'network/JS error'}`, false);
    }
  };

  const viewWaterfall = async (contractId: number) => {
    try {
      const res = await authFetch(`${API}/waterfall/${contractId}`);
      if (res.ok) { setWaterfall(await res.json()); }
    } catch { showToast('Waterfall load failed', false); }
  };

  // Phase 2f follow-on — Journal Entries audit view for a revenue contract
  const viewJEs = async (contractId: number) => {
    try {
      const res = await authFetch(`${REV_API}/contracts/${contractId}/journal-entries`);
      if (res.ok) {
        setJeList(await res.json());
        setExpandedJe(null);
      } else {
        const e = await res.json();
        showToast(e.detail || 'Failed to load journal entries', false);
      }
    } catch {
      showToast('JE load failed', false);
    }
  };
  const closeJEs = () => { setJeList(null); setExpandedJe(null); };

  // Phase 2f — Confirm Milestone (ASC 606 PIT obligation fulfillment)
  const openFulfill = (ob: Obligation, contract: Contract) => {
    setFulfillTarget({ ob, contract });
    setFulfillDate(new Date().toISOString().slice(0, 10));
    setFulfillNotes('');
  };
  const closeFulfill = () => { setFulfillTarget(null); setFulfillNotes(''); };
  const submitFulfill = async () => {
    if (!fulfillTarget) return;
    if (!fulfillNotes.trim()) { showToast('Evidence notes are required', false); return; }
    setFulfilling(true);
    try {
      const res = await authFetch(`${REV_API}/obligations/${fulfillTarget.ob.id}/fulfill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fulfillment_date: fulfillDate,
          fulfillment_notes: fulfillNotes.trim(),
        }),
      });
      if (res.ok) {
        const d = await res.json();
        showToast(`Recognized ${formatAmount(fulfillTarget.ob.allocated_price)} for ${fulfillTarget.ob.obligation_name || `POB #${fulfillTarget.ob.po_number}`}`, true);
        closeFulfill();
        load();
      } else {
        const e = await res.json();
        showToast(e.detail || 'Fulfillment failed', false);
      }
    } catch (e) {
      showToast('Request failed', false);
    } finally {
      setFulfilling(false);
    }
  };

  return (
    <PageLayout title="Revenue Recognition" subtitle="ASC 606 contract management, performance obligations, and deferred revenue waterfall">
      {loading ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</div>
      ) : contracts.length === 0 ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>No revenue contracts found. Create contracts from the AR Invoices page.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {contracts.map(c => {
            const isExpanded = expandedContractId === c.id;
            const pobCount = (c.obligations || []).length;
            const totalAllocated = (c.obligations || []).reduce((s, ob) => s + (ob.allocated_price || 0), 0);
            const totalRecognized = (c.obligations || []).reduce((s, ob) => s + (ob.amount_recognized || 0), 0);
            const openPobs = (c.obligations || []).filter(ob => ob.status === 'OPEN').length;
            const completePobs = (c.obligations || []).filter(ob => ob.status === 'COMPLETE').length;
            const pctRecognized = totalAllocated > 0 ? Math.round((totalRecognized / totalAllocated) * 100) : 0;
            return (
              <div key={c.id} style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
                <div
                  onClick={() => setExpandedContractId(isExpanded ? null : c.id)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 16,
                    cursor: 'pointer',
                    background: isExpanded ? 'var(--accent-subtle, #f0f4f8)' : 'transparent',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = 'var(--bg-hover, #f8fafc)'; }}
                  onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                    <span style={{ fontSize: 14, color: 'var(--text-tertiary)', width: 14, flexShrink: 0 }}>
                      {isExpanded ? '▾' : '▸'}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--accent)' }}>{c.contract_number}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{c.customer_name || c.customer_id} — {formatAmount(c.total_value)} {c.currency}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>{c.start_date} to {c.end_date} {c.asc606_step1_met && <span style={{ color: 'var(--positive)', fontWeight: 600 }}>ASC 606 Step 1 MET</span>}</div>
                    </div>
                  </div>

                  {/* Roll-up: POB count, totals, progress — visible at a glance without expanding */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginRight: 16 }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>POBs</div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{pobCount}</div>
                      {pobCount > 0 && (
                        <div style={{ fontSize: 9, color: 'var(--text-secondary)' }}>
                          {completePobs} done · {openPobs} open
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Allocated</div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{formatAmount(totalAllocated)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Recognized</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: totalRecognized > 0 ? 'var(--positive)' : 'var(--text-secondary)' }}>{formatAmount(totalRecognized)}</div>
                      <div style={{ fontSize: 9, color: 'var(--text-secondary)' }}>{pctRecognized}%</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                    <ActionButton label="Journal Entries" onClick={() => viewJEs(c.id)} variant="secondary" size="sm" />
                    <ActionButton label="Waterfall" onClick={() => viewWaterfall(c.id)} variant="secondary" size="sm" />
                  </div>
                </div>

                {isExpanded && c.obligations && c.obligations.length > 0 && (
                  <div style={{ padding: '0 16px 16px 16px', borderTop: '1px solid var(--border-subtle)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                      <thead><tr>
                        {['#', 'Description', 'Method', 'SSP', 'Allocated', 'Recognized', 'Status', 'Actions'].map(h => (
                          <th key={h} style={{ ...thStyle, textAlign: (['SSP', 'Allocated', 'Recognized'] as string[]).includes(h) ? 'right' : 'left' }}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>{c.obligations.map(ob => (
                        <tr key={ob.id}>
                          <td style={tdStyle}>{ob.po_number}</td>
                          <td style={tdStyle}>
                            <div>{ob.description}</div>
                            {ob.fulfillment_trigger && <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{ob.fulfillment_trigger}{ob.fulfilled_at ? ` · fulfilled ${ob.fulfilled_at.slice(0, 10)}` : ''}</div>}
                          </td>
                          <td style={{ ...tdStyle, fontSize: 11 }}>{ob.recognition_method}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(ob.standalone_selling_price)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatAmount(ob.allocated_price)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: ob.amount_recognized > 0 ? 'var(--positive)' : 'var(--text-secondary)' }}>{formatAmount(ob.amount_recognized)}</td>
                          <td style={tdStyle}><StatusBadge status={ob.status} /></td>
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', gap: 4 }}>
                              {ob.recognition_method === 'POINT_IN_TIME' && ob.status === 'OPEN' && <ActionButton label="Confirm Milestone" onClick={() => openFulfill(ob, c)} variant="success" size="sm" />}
                              {ob.recognition_method !== 'POINT_IN_TIME' && <ActionButton label="Schedule" onClick={() => viewSchedule(ob.id)} variant="secondary" size="sm" />}
                              {ob.recognition_method !== 'POINT_IN_TIME' && ob.status === 'OPEN' && <ActionButton label="Generate" onClick={() => handleGenerate(ob.id)} variant="success" size="sm" />}
                            </div>
                          </td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Schedule Modal */}
      <Modal open={!!selectedSchedule} onClose={() => setSelectedSchedule(null)} title={`Recognition Schedule${selectedSchedule ? ` — ${selectedSchedule.description || ''}` : ''}`} size="lg" footer={<ActionButton label="Close" onClick={() => setSelectedSchedule(null)} variant="secondary" />}>
        {selectedSchedule && (
          <div>
            <KpiGrid columns={4} items={[
              { label: 'Allocated', value: formatAmountCompact(selectedSchedule.allocated_price) },
              { label: 'Recognized', value: formatAmountCompact(selectedSchedule.total_recognized), color: 'var(--positive)' },
              { label: 'Remaining', value: formatAmountCompact(selectedSchedule.total_remaining), color: 'var(--warning)' },
              { label: 'Progress', value: `${selectedSchedule.pct_recognized}%`, color: 'var(--accent)' },
            ]} />
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                {['Period', 'Scheduled', 'Recognized', 'Status', 'Action'].map(h => (
                  <th key={h} style={{ ...thStyle, textAlign: (['Scheduled', 'Recognized'] as string[]).includes(h) ? 'right' : 'left' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>{selectedSchedule.lines.map(l => (
                <tr key={l.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{l.period}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(l.scheduled_amount)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: l.recognized_amount > 0 ? 'var(--positive)' : 'var(--text-secondary)' }}>{formatAmount(l.recognized_amount)}</td>
                  <td style={tdStyle}><StatusBadge status={l.status} /></td>
                  <td style={tdStyle}>
                    {l.status === 'SCHEDULED' && <ActionButton label="Recognize" onClick={() => handleRecognize(selectedSchedule.obligation_id, l.period)} variant="success" size="sm" />}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* Waterfall Modal — MSA-aware */}
      <Modal
        open={!!waterfall}
        onClose={() => setWaterfall(null)}
        title={waterfall
          ? `${waterfall.is_msa ? 'MSA Utilization Waterfall' : 'Revenue Recognition Waterfall'} — ${waterfall.contract_number}`
          : 'Revenue Waterfall'}
        size="lg"
        footer={<ActionButton label="Close" onClick={() => setWaterfall(null)} variant="secondary" />}
      >
        {waterfall && (
          <div>
            {/* KPIs differ by contract subtype.
                MSA: Committed / Drawn / Recognized / Unutilized Ceiling
                Non-MSA: Contract Value / Recognized / Deferred / Progress */}
            {waterfall.is_msa ? (
              <KpiGrid columns={4} items={[
                { label: 'Committed (Ceiling)', value: formatAmountCompact(waterfall.committed_amount ?? waterfall.total_value) },
                { label: 'Drawn to POBs', value: formatAmountCompact(waterfall.allocated_to_pobs ?? 0), color: 'var(--accent)' },
                { label: 'Recognized', value: formatAmountCompact(waterfall.total_recognized), color: 'var(--positive)' },
                { label: 'Unutilized Ceiling', value: formatAmountCompact(waterfall.unutilized_ceiling ?? 0), color: 'var(--text-secondary)' },
              ]} />
            ) : (
              <KpiGrid columns={4} items={[
                { label: 'Contract Value', value: formatAmountCompact(waterfall.total_value) },
                { label: 'Recognized', value: formatAmountCompact(waterfall.total_recognized), color: 'var(--positive)' },
                { label: 'Deferred Revenue', value: formatAmountCompact(waterfall.total_deferred), color: 'var(--warning)' },
                { label: 'Progress', value: `${waterfall.pct_recognized}%`, color: 'var(--accent)' },
              ]} />
            )}

            {waterfall.is_msa && (
              <div style={{ marginTop: 8, marginBottom: 12, padding: '8px 12px', background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.25)', borderRadius: 6, fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                <strong>MSA note:</strong> "Unutilized Ceiling" is remaining commitment headroom, NOT deferred revenue.
                Deferred revenue on an MSA is only the portion of already-drawn POBs not yet recognized:&nbsp;
                <strong style={{ color: 'var(--warning)' }}>{formatAmount(waterfall.true_deferred ?? 0)}</strong>.
                Progress shown is recognized / drawn, not recognized / ceiling.
              </div>
            )}

            {waterfall.obligations.map(ob => {
              const isPIT = (ob as any).recognition_method === 'POINT_IN_TIME' || ob.periods.length === 0;
              return (
                <div key={ob.obligation_id} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                    {ob.description} — {formatAmount(ob.allocated_price)} ({ob.status})
                  </div>
                  {ob.periods.length > 0 ? (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {ob.periods.map(p => {
                        // ASC 606 Phase 2f — show projected-vs-actual deferred balance.
                        // Legacy field 'deferred_balance' aliases actual for backward compat.
                        const projected = p.projected_deferred ?? p.deferred_balance;
                        const actual = p.actual_deferred ?? p.deferred_balance;
                        const diverged = Math.abs(projected - actual) > 0.01;
                        return (
                          <div key={p.period} style={{ padding: '6px 8px', borderRadius: 4, background: p.status === 'RECOGNIZED' ? 'var(--positive-bg, #f0fdf4)' : 'var(--bg-card)', border: `1px solid ${p.status === 'RECOGNIZED' ? 'var(--positive)' : 'var(--border-subtle)'}`, textAlign: 'center', minWidth: 86 }}>
                            <div style={{ fontSize: 9, color: 'var(--text-tertiary)' }}>{p.period}</div>
                            <div style={{ fontSize: 11, fontWeight: 600 }}>{formatAmount(p.scheduled)}</div>
                            <div style={{ fontSize: 9, color: 'var(--text-secondary)' }}>Proj: {formatAmount(projected)}</div>
                            <div style={{ fontSize: 9, color: diverged ? 'var(--warning)' : 'var(--text-secondary)' }}>Actual: {formatAmount(actual)}</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                      {isPIT
                        ? 'POINT_IN_TIME obligation — no schedule; recognized on milestone confirmation.'
                        : 'No schedule generated yet.'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      {/* Phase 2f — Confirm Milestone modal (ASC 606 PIT obligation fulfillment) */}
      <Modal
        open={!!fulfillTarget}
        onClose={closeFulfill}
        title={fulfillTarget ? `Confirm Milestone — ${fulfillTarget.ob.obligation_name || `POB #${fulfillTarget.ob.po_number}`}` : 'Confirm Milestone'}
        size="md"
        footer={<>
          <ActionButton label="Cancel" onClick={closeFulfill} variant="secondary" disabled={fulfilling} />
          <ActionButton label={fulfilling ? 'Posting…' : 'Confirm & Post JE'} onClick={submitFulfill} variant="success" disabled={fulfilling || !fulfillNotes.trim()} />
        </>}
      >
        {fulfillTarget && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ padding: 12, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Contract</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{fulfillTarget.contract.contract_number} — {fulfillTarget.contract.customer_name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6 }}>{fulfillTarget.ob.description}</div>
            </div>
            <div style={{ padding: 12, background: 'var(--warning-bg, #fffbeb)', border: '1px solid var(--warning)', borderRadius: 6, fontSize: 12 }}>
              <div style={{ fontWeight: 600, color: 'var(--warning)', marginBottom: 4 }}>This will post a revenue recognition JE</div>
              <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)' }}>
                DR 2200-100 Deferred Revenue&nbsp;&nbsp;{formatAmount(fulfillTarget.ob.allocated_price)}<br />
                &nbsp;&nbsp;&nbsp;&nbsp;CR 4100-100 Revenue&nbsp;&nbsp;{formatAmount(fulfillTarget.ob.allocated_price)}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 6 }}>Authority gate: REVENUE_RECOGNITION category, DOA threshold applied to allocated amount.</div>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Fulfillment Date (when control transferred) *</label>
              <input
                type="date"
                title="Fulfillment date"
                aria-label="Fulfillment date"
                value={fulfillDate}
                onChange={e => setFulfillDate(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Evidence Notes * <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(for ASC 606 audit trail)</span></label>
              <textarea
                value={fulfillNotes}
                onChange={e => setFulfillNotes(e.target.value)}
                placeholder="e.g. Customer signed acceptance letter delivered via email 2026-04-24. Attached PDF in /contracts/global-petro/process-engineering-acceptance.pdf"
                rows={4}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', resize: 'vertical' }}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Phase 2f follow-on — Journal Entries audit modal */}
      <Modal
        open={!!jeList}
        onClose={closeJEs}
        title={jeList ? `Journal Entries — ${jeList.contract_number} (${jeList.count})` : 'Journal Entries'}
        size="lg"
        footer={<ActionButton label="Close" onClick={closeJEs} variant="secondary" />}
      >
        {jeList && (
          jeList.count === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-hint)' }}>No journal entries posted for this contract yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* Summary bar: totals by category */}
              {(() => {
                const billing = jeList.journal_entries.filter(j => j.category === 'BILLING');
                const recognition = jeList.journal_entries.filter(j => j.category === 'RECOGNITION');
                const billTotal = billing.reduce((s, j) => s + j.total_amount, 0);
                const recTotal = recognition.reduce((s, j) => s + j.total_amount, 0);
                return (
                  <KpiGrid columns={4} items={[
                    { label: 'Total JEs', value: jeList.count },
                    { label: 'Billing JEs', value: billing.length, color: 'var(--accent)' },
                    { label: 'Recognition JEs', value: recognition.length, color: 'var(--positive)' },
                    { label: 'Total Posted', value: formatAmountCompact(billTotal + recTotal) },
                  ]} />
                );
              })()}
              {jeList.journal_entries.map(je => (
                <div key={je.id} style={{ border: '1px solid var(--border-subtle)', borderRadius: 6, overflow: 'hidden' }}>
                  <div
                    style={{ padding: '10px 14px', background: 'var(--bg-card)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
                    onClick={() => setExpandedJe(expandedJe === je.id ? null : je.id)}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2 }}>
                        <span style={{
                          fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3, letterSpacing: '0.03em',
                          background: je.category === 'BILLING' ? 'var(--accent-bg, #eff6ff)' : 'var(--positive-bg, #f0fdf4)',
                          color: je.category === 'BILLING' ? 'var(--accent)' : 'var(--positive)',
                          border: `1px solid ${je.category === 'BILLING' ? 'var(--accent)' : 'var(--positive)'}`,
                        }}>{je.category}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'monospace' }}>{je.je_number}</span>
                        <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{je.posting_date}</span>
                        <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>entity {je.entity_code}</span>
                        <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>by {je.posted_by}</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{je.description}</div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{formatAmount(je.total_amount)} {je.currency}</div>
                    <div style={{ fontSize: 14, color: 'var(--text-tertiary)' }}>{expandedJe === je.id ? '▼' : '▶'}</div>
                  </div>
                  {expandedJe === je.id && (
                    <div style={{ padding: '8px 14px 12px', background: 'var(--bg-card)', borderTop: '1px solid var(--border-subtle)' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                          <tr>
                            <th style={{ ...thStyle, padding: '6px 8px' }}>Account</th>
                            <th style={{ ...thStyle, padding: '6px 8px' }}>Description</th>
                            <th style={{ ...thStyle, padding: '6px 8px', textAlign: 'right' }}>Debit</th>
                            <th style={{ ...thStyle, padding: '6px 8px', textAlign: 'right' }}>Credit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {je.lines.map(l => (
                            <tr key={l.line_number}>
                              <td style={{ ...tdStyle, padding: '6px 8px', fontFamily: 'monospace', fontWeight: 600 }}>{l.account_code}</td>
                              <td style={{ ...tdStyle, padding: '6px 8px', color: 'var(--text-secondary)' }}>{l.description}</td>
                              <td style={{ ...tdStyle, padding: '6px 8px', textAlign: 'right', color: l.debit_amount > 0 ? 'var(--text-primary)' : 'var(--text-hint)', fontWeight: l.debit_amount > 0 ? 600 : 400 }}>
                                {l.debit_amount > 0 ? formatAmount(l.debit_amount) : '—'}
                              </td>
                              <td style={{ ...tdStyle, padding: '6px 8px', textAlign: 'right', color: l.credit_amount > 0 ? 'var(--text-primary)' : 'var(--text-hint)', fontWeight: l.credit_amount > 0 ? 600 : 400 }}>
                                {l.credit_amount > 0 ? formatAmount(l.credit_amount) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </Modal>

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
